import LoginForm from '@/views/LoginForm.vue'
import SignupForm from '@/views/SignupForm.vue'
import VerifyPage from '@/views/VerifyPage.vue'
import ForgotPasswordForm from '@/views/ForgotPasswordForm.vue'
import ResetPasswordForm from '@/views/ResetPasswordForm.vue'
import ChangePasswordForm from '@/views/ChangePasswordForm.vue'
import AdminPublishingDashboard from '@/views/AdminPublishingDashboard.vue'
import AdminVideoFileDashboard from '@/views/AdminVideoFileDashboard.vue'
import NewVideo from '@/views/NewVideo.vue'
import EditVideo from '@/views/EditVideo.vue'
import AdminOrganizationDashboard from '@/views/AdminOrganizationDashboard.vue'
import ChannelForm from '@/views/ChannelForm.vue'
import AdminUserDashboard from '@/views/AdminUserDashboard.vue'
import UserForm from '@/views/UserForm.vue'
import AdminDebugTools from '@/views/AdminDebugTools.vue'
import VideoDashboard from '@/views/VideoDashboard.vue'
import OrganizationForm from '@/views/OrganizationForm.vue'
import UserDetails from '@/views/UserDetails.vue'
import OrganizationDetails from '@/views/OrganizationDetails.vue'
import OrganizationMemberForm from '@/views/OrganizationMemberForm.vue'
import ChannelDetails from '@/views/ChannelDetails.vue'
import VideoFileDashboard from '@/views/VideoFileDashboard.vue'
import AdminChannelDashboard from '@/views/AdminChannelDashboard.vue'
import VideoDetails from '@/views/VideoDetails.vue'
import AdminPermissionList from '@/views/AdminPermissionList.vue'
import RoleForm from '@/views/RoleForm.vue'
import OrganizationDashboard from '@/views/OrganizationDashboard.vue'
import VideoFileForm from '@/views/VideoFileForm.vue'
import VideoFileDetails from '@/views/VideoFileDetails.vue'
import NewVideoFile from '@/views/NewVideoFile.vue'
import EncodeVideoFile from '@/views/EncodeVideoFile.vue'
import PublicVideoPreview from '@/views/PublicVideoPreview.vue'

import { createRouter, createWebHashHistory } from 'vue-router'
import { authState } from '@/scripts/services/authService'
import { isSuperuser } from '@/scripts/permissions'

// Navigation guards
const requireAuth = (to, from, next) => {
  if (authState.isLoggedIn.value) {
    next()
  } else {
    next('/')
  }
}

const requireSuperuser = (to, from, next) => {
  if (isSuperuser()) {
    next()
  } else {
    next('/')
  }
}

const redirectIfLoggedIn = (to, from, next) => {
  if (authState.isLoggedIn.value) {
    next('/')
  } else {
    next()
  }
}

const routes = [
  // Default route
  {
    path: '/',
    redirect: () => {
      if (isSuperuser()) {
        return '/admin/videofiles'
      } else if (authState.isLoggedIn.value) {
        return '/filemanager'
      } else {
        return '/login'
      }
    }
  },
  // Public routes
  {
    path: '/share/video/:short_id',
    name: 'Shared Video',
    component: PublicVideoPreview
  },
  // Authentication, login, signup, account operations
  {
    path: '/login',
    name: 'Login',
    component: LoginForm,
    beforeEnter: redirectIfLoggedIn
  },
  {
    path: '/signup',
    name: 'Signup',
    component: SignupForm,
    beforeEnter: redirectIfLoggedIn
  },
  {
    path: '/verify/:user_id/:token',
    name: 'Verify',
    component: VerifyPage
  },
  {
    path: '/forgotpassword',
    name: 'Forgot Password',
    component: ForgotPasswordForm,
    beforeEnter: redirectIfLoggedIn
  },
  {
    path: '/resetpassword/:user_id/:token',
    name: 'ResetPassword',
    component: ResetPasswordForm,
    beforeEnter: redirectIfLoggedIn
  },
  {
    path: '/changepassword',
    name: 'Change Password',
    component: ChangePasswordForm,
    beforeEnter: requireAuth
  },
  // Customer routes
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: VideoDashboard,
    beforeEnter: requireAuth
  },
  {
    path: '/filemanager',
    name: 'File Manager',
    component: VideoFileDashboard,
    beforeEnter: requireAuth
  },
  {
    path: '/publishing',
    name: 'Publishing',
    component: VideoDashboard,
    beforeEnter: requireAuth
  },
  {
    path: '/organization',
    name: 'Organization',
    component: OrganizationDashboard,
    beforeEnter: requireAuth
  },
  {
    path: '/users/:user_id',
    name: 'User Details',
    component: UserDetails,
    beforeEnter: requireAuth
  },
  {
    path: '/users/:user_id/edit',
    name: 'Edit User',
    component: UserForm,
    beforeEnter: requireAuth
  },
  {
    path: '/organizations/:organization_id',
    name: 'Organization Details',
    component: OrganizationDetails,
    beforeEnter: requireAuth
  },
  {
    path: '/organizations/:organization_id/members/:user_id',
    name: 'Organization Member Details',
    component: OrganizationMemberForm,
    beforeEnter: requireAuth,
    props: true
  },
  {
    path: '/organizations/new',
    name: 'New Organization',
    component: OrganizationForm,
    beforeEnter: requireAuth
  },
  {
    path: '/organizations/:organization_id/edit',
    name: 'Edit Organization',
    component: OrganizationForm,
    beforeEnter: requireAuth
  },
  {
    path: '/channels/:channel_id',
    name: 'Channel Details',
    component: ChannelDetails,
    beforeEnter: requireAuth
  },
  {
    path: '/channels/:channel_id/edit',
    name: 'Edit Channel',
    component: ChannelForm,
    beforeEnter: requireAuth
  },
  {
    path: '/channels/:channel_id/videos/new',
    name: 'Channel: New Video',
    component: NewVideo,
    beforeEnter: requireAuth
  },
  {
    path: '/channels/:channel_id/videofiles/new',
    name: 'Channel: New Video File',
    component: NewVideoFile,
    beforeEnter: requireAuth
  },
  {
    path: '/videos/:video_id',
    name: 'Video Details',
    component: VideoDetails,
    beforeEnter: requireAuth
  },
  {
    path: '/videos/:video_id/edit',
    name: 'Edit Video',
    component: EditVideo,
    beforeEnter: requireAuth
  },
  {
    path: '/videofiles/:video_file_id',
    name: 'Video File Details',
    component: VideoFileDetails,
    beforeEnter: requireAuth
  },
  {
    path: '/videofiles/:video_file_id/encode',
    name: 'Encode Video File',
    component: EncodeVideoFile,
    beforeEnter: requireAuth
  },
  // Admin routes
  {
    path: '/admin/videofiles',
    name: 'Admin: Video File Dashboard',
    component: AdminVideoFileDashboard,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/videofiles/new',
    name: 'Admin: New Video File',
    component: NewVideoFile,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/videofiles/:video_file_id/edit',
    name: 'Admin: Edit Video File',
    component: VideoFileForm,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/publishing',
    name: 'Admin: Publishing Dashboard',
    component: AdminPublishingDashboard,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/publishing/new',
    name: 'Admin: Publish Video',
    component: NewVideo,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/channels',
    name: 'Admin: Channel Dashboard',
    component: AdminChannelDashboard,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/channels/new',
    name: 'Admin: New Channel',
    component: ChannelForm,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/organizations',
    name: 'Admin: Organization Dashboard',
    component: AdminOrganizationDashboard,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/users',
    name: 'Admin: User Dashboard',
    component: AdminUserDashboard,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/users/new',
    name: 'Admin: New User',
    component: UserForm,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/permissions',
    name: 'Admin: Permissions Dashboard',
    component: AdminPermissionList,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/permissions/roles/new',
    name: 'Adming: New Role',
    component: RoleForm,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/permissions/roles/:id/edit',
    name: 'Admin: Edit Role',
    component: RoleForm,
    beforeEnter: requireSuperuser
  },
  {
    path: '/admin/debugtools',
    name: 'Admin Debug Tools',
    component: AdminDebugTools,
    beforeEnter: requireSuperuser
  },
]

const router = createRouter({
  history: createWebHashHistory(),
  routes: routes
})

export default router
